import { Vector3 } from 'three'
import type { TuningConfig } from './config'
import type { Pose } from '../input/types'

/**
 * Estimates the pilot's centre of mass in **tracking space** (DESIGN.md §2).
 *
 * The headset only knows head + hands, so this is a model, not a measurement.
 * Two decisions carry all the weight:
 *
 *  - Head *position*, never head *orientation*. Tilting your head barely moves
 *    your real CoM; leaning your torso moves head and CoM together. Using
 *    orientation would make cocking your head sideways roll the rocket.
 *  - Lean gain < 1. When you lean you pivot at the ankles, so your head (end of
 *    the lever) displaces further than your CoM does.
 */
export class CoMEstimator {
  /** Smoothed CoM in tracking space. */
  readonly value = new Vector3()

  /** Standing head height recorded at calibration. */
  standingHeight: number
  /** Head XZ at calibration — the "not leaning" reference. */
  private readonly neutral = new Vector3()
  private readonly target = new Vector3()
  private calibrated = false

  constructor(nominalHeight: number, comHeightFraction: number) {
    this.standingHeight = nominalHeight
    this.value.set(0, nominalHeight * comHeightFraction, 0)
    this.target.copy(this.value)
  }

  /** Height of the CoM above the tracking-space floor. */
  get height(): number {
    return this.value.y
  }

  /**
   * Snapshot the player standing neutral. Called on session start ("press A")
   * and any time the player re-centres.
   */
  calibrate(head: Pose, cfg: TuningConfig): void {
    this.standingHeight = head.tracked ? head.position.y : cfg.nominalHeight
    this.neutral.set(head.position.x, 0, head.position.z)
    const y = this.standingHeight * cfg.comHeightFraction
    this.value.set(this.neutral.x, y, this.neutral.z)
    this.target.copy(this.value)
    this.calibrated = true
  }

  update(head: Pose, cfg: TuningConfig, dt: number): Vector3 {
    if (!this.calibrated) this.calibrate(head, cfg)

    const y = this.standingHeight * cfg.comHeightFraction
    if (head.tracked && cfg.comLeanGain > 0) {
      this.target.set(
        this.neutral.x + cfg.comLeanGain * (head.position.x - this.neutral.x),
        y,
        this.neutral.z + cfg.comLeanGain * (head.position.z - this.neutral.z),
      )
    } else {
      this.target.set(this.neutral.x, y, this.neutral.z)
    }

    // Exponential smoothing, frame-rate independent. Doubles as a comfort
    // filter: the CoM is the rig's rotation pivot, so a jumpy estimate would
    // translate the whole world.
    const alpha = cfg.comSmoothing > 1e-4 ? 1 - Math.exp(-dt / cfg.comSmoothing) : 1
    this.value.lerp(this.target, alpha)
    return this.value
  }

  reset(cfg: TuningConfig): void {
    this.value.set(this.neutral.x, this.standingHeight * cfg.comHeightFraction, this.neutral.z)
    this.target.copy(this.value)
  }
}
